/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import { useContext } from 'react'

import Icon from '../../components/Icon'
import { Flex } from '../../components/Layout'
import { media } from '../../components/Responsive'
import { SearchContext } from './SearchContextProvider'
import { clearRecentSearchTerm, storeRecentSearchItem } from './recent-search'
import { PlayerRow, TeamRow, SchedRow, OrgRow, VenueRow } from './SearchRow'
import Text from '../../components/ts-components/Text'
import { isCanlan } from '@sportninja/common/utils/customer-name'

const SearchRecentPanel = ({ recentSearches, setRecentSearches, onClose }) => {
  const searchContext = useContext(SearchContext)

  const hasRecentSearches =
    recentSearches?.terms?.length > 0 || recentSearches?.items?.length > 0

  if (!hasRecentSearches) {
    return (
      <Flex
        noFlex
        alignItems='center'
        justifyContent='center'
        css={css`
          width: 100%;
          height: 100%;
        `}
      >
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
        >
          Search For {isCanlan ? 'Leagues' : 'Organizations'},{' '}
          {isCanlan ? 'Seasons' : 'Competitions'}, Teams or Players
        </Text>
      </Flex>
    )
  }

  return (
    <Flex
      noFlex
      css={css`
        width: 100%;
        max-width: 1280px;
        padding: 16px;
        flex-direction: column;
        overflow-y: auto;

        ${media.mobile} {
          flex-direction: column;
        }
      `}
    >
      {recentSearches?.terms?.length > 0 && (
        <div
          css={css`
            min-width: 320px;
          `}
        >
          <Text
            variant='paragraphXSmall'
            weight='semiBold'
            color={colors.NEUTRAL_50}
            style={css`
              margin-bottom: 8px;
            `}
          >
            Recent Searches
          </Text>
          {recentSearches.terms.map((recent, idx) => {
            return (
              <Flex
                alignItems='center'
                justifyContent='space-between'
                key={`${recent}-${idx}`}
                onClick={() => {
                  searchContext.setText(recent)
                  searchContext.searchInputRef.current.focus()
                }}
                css={css`
                  cursor: pointer;
                  padding: 8px 16px 8px 8px;
                  margin: 4px 0;
                  font-size: 16px;
                  font-weight: 500;

                  &:nth-of-type(even) {
                    background-color: #292e35;
                  }

                  transition: background-color 0.2s ease-in-out;

                  &:hover {
                    background-color: rgba(255, 255, 255, 0.1);
                  }
                `}
              >
                <Text
                  variant='paragraphXSmall'
                  weight='semiBold'
                  color={colors.NEUTRAL_50}
                >
                  {recent}
                </Text>
                <button
                  type='button'
                  css={css`
                    margin-left: 8px;
                    border-radius: 8px;
                    width: 24px;
                    height: 24px;
                    pointer-events: all;

                    transition: all 0.2s ease-in-out;
                    &:hover {
                      background-color: rgba(255, 255, 255, 0.5);
                      i {
                        color: black;
                      }
                    }
                  `}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()

                    const store = clearRecentSearchTerm(recent)
                    setRecentSearches(store)
                  }}
                >
                  <Icon
                    color={colors.ATTENDANCE_GRAY}
                    faType='fal'
                    name='times'
                    fontSize={16}
                  />
                </button>
              </Flex>
            )
          })}
        </div>
      )}
      {recentSearches?.items?.length > 0 && (
        <div
          css={css`
            margin-top: 16px;
            flex: 1;
          `}
        >
          <Text
            variant='paragraphXSmall'
            weight='semiBold'
            color={colors.NEUTRAL_50}
            style={css`
              margin-bottom: 8px;
            `}
          >
            Previous Results
          </Text>
          <div>
            {recentSearches?.items &&
              recentSearches?.items?.map((item) => {
                const onClick = () => {
                  onClose()
                  // Wait until the search menu disappears, so we don't partially
                  // display the result moving to the top of the list
                  setTimeout(() => {
                    if (item) {
                      const store = storeRecentSearchItem(item)
                      setRecentSearches(store)
                    }
                  }, 200)
                }

                const entityType = item?.entityType || item?.type
                switch (entityType) {
                  case ENTITY_TYPES.player:
                    return (
                      <PlayerRow
                        key={`${item?.id}-${entityType}`}
                        item={item}
                        onClick={onClick}
                      />
                    )
                  case ENTITY_TYPES.team:
                  case 'team':
                    return (
                      <TeamRow
                        key={`${item?.id}-${entityType}`}
                        item={item}
                        onClick={onClick}
                      />
                    )
                  case ENTITY_TYPES.schedule:
                    return (
                      <SchedRow
                        key={`${item?.id}-${entityType}`}
                        item={item}
                        onClick={onClick}
                      />
                    )
                  case ENTITY_TYPES.org:
                    return (
                      <OrgRow
                        key={`${item?.id}-${entityType}`}
                        item={item}
                        onClick={onClick}
                      />
                    )
                  case ENTITY_TYPES.venue:
                    return (
                      <VenueRow
                        key={`${item?.id}-${entityType}`}
                        item={item}
                        onClick={onClick}
                      />
                    )
                  default:
                    return null
                }
              })}
          </div>
        </div>
      )}
    </Flex>
  )
}

export default SearchRecentPanel
